import type { Requirement, Meeting, Application } from '@/types'

export function formatDateValue(date: unknown): string {
  if (!date) return ''
  // Handle Firestore Timestamps
  if (date && typeof date === 'object' && 'toDate' in date && typeof (date as { toDate: () => Date }).toDate === 'function') {
    return (date as { toDate: () => Date }).toDate().toLocaleDateString('es-CL')
  }
  if (date instanceof Date) {
    return date.toLocaleDateString('es-CL')
  }
  return String(date)
}

export function buildRequirementContent(req: Requirement): string {
  let content = `**${req.key} - ${req.title}**\n\n`
  content += `- Tipo: ${req.type}\n`
  content += `- Prioridad: ${req.priority}\n`
  content += `- Estado Definicion: ${req.definitionStatus}\n`
  content += `- Estado Desarrollo: ${req.developmentStatus}\n`

  if (req.description) {
    content += `\n**Descripcion:**\n${req.description}\n`
  }

  if (req.userStory) {
    content += `\n**User Story:**\n`
    content += `Como ${req.userStory.asA}, quiero ${req.userStory.iWant} para ${req.userStory.soThat}\n`
  }

  if (req.storyPoints) content += `\n- Story Points: ${req.storyPoints}\n`
  if (req.estimatedHours) content += `- Horas Estimadas: ${req.estimatedHours}\n`
  if (req.actualHours) content += `- Horas Reales: ${req.actualHours}\n`

  if (req.dueDate) content += `- Fecha Limite: ${formatDateValue(req.dueDate)}\n`
  if (req.startedAt) content += `- Inicio: ${formatDateValue(req.startedAt)}\n`
  if (req.completedAt) content += `- Completado: ${formatDateValue(req.completedAt)}\n`

  if (req.acceptanceCriteria?.length) {
    content += `\n**Criterios de Aceptacion:**\n`
    req.acceptanceCriteria.forEach((ac) => {
      content += `- [${ac.completed ? 'x' : ' '}] ${ac.description}\n`
    })
  }

  if (req.definitionOfDone?.length) {
    content += `\n**Definition of Done:**\n`
    req.definitionOfDone.forEach((dod) => {
      content += `- [${dod.completed ? 'x' : ' '}] ${dod.text}\n`
    })
  }

  if (req.clientNotes) {
    content += `\n**Notas del Cliente:**\n${req.clientNotes}\n`
  }
  if (req.internalNotes) {
    content += `\n**Notas Internas:**\n${req.internalNotes}\n`
  }

  if (req.labels?.length) {
    content += `\n- Labels: ${req.labels.join(', ')}\n`
  }

  return content
}

export function buildMeetingContent(meeting: Meeting): string {
  let content = `**${meeting.title}**\n\n`
  content += `- Tipo: ${meeting.meetingType}\n`
  content += `- Estado: ${meeting.status}\n`

  if (meeting.scheduledDate) content += `- Fecha: ${formatDateValue(meeting.scheduledDate)}\n`
  if (meeting.startTime) content += `- Hora Inicio: ${meeting.startTime}\n`
  if (meeting.endTime) content += `- Hora Fin: ${meeting.endTime}\n`
  if (meeting.duration) content += `- Duracion: ${meeting.duration} minutos\n`
  if (meeting.location) content += `- Ubicacion: ${meeting.location}\n`

  if (meeting.description) {
    content += `\n**Descripcion:**\n${meeting.description}\n`
  }

  if (meeting.attendees?.length) {
    content += `\n**Asistentes:**\n`
    meeting.attendees.forEach((att) => {
      content += `- ${att.name} (${att.role})`
      if (att.rsvpStatus) content += ` - RSVP: ${att.rsvpStatus}`
      if (att.attended !== undefined) content += att.attended ? ' [Asistio]' : ' [No asistio]'
      content += '\n'
    })
  }

  if (meeting.agenda) {
    content += `\n**Agenda:**\n${meeting.agenda}\n`
  }

  if (meeting.notes) {
    content += `\n**Notas:**\n${meeting.notes}\n`
  }

  if (meeting.decisions?.length) {
    content += `\n**Decisiones:**\n`
    meeting.decisions.forEach((d) => {
      content += `- ${d.description}`
      if (d.decidedBy) content += ` (decidido por: ${d.decidedBy})`
      content += '\n'
    })
  }

  if (meeting.actionItems?.length) {
    content += `\n**Accionables:**\n`
    meeting.actionItems.forEach((ai) => {
      content += `- [${ai.status}] ${ai.description}`
      if (ai.assigneeName) content += ` (responsable: ${ai.assigneeName})`
      if (ai.dueDate) content += ` - fecha limite: ${formatDateValue(ai.dueDate)}`
      content += '\n'
    })
  }

  if (meeting.tags?.length) {
    content += `\n- Tags: ${meeting.tags.join(', ')}\n`
  }

  return content
}

export function buildApplicationContent(app: Application): string {
  let content = `**${app.name}**\n\n`
  content += `- Plataforma: ${app.platform}\n`
  content += `- Estado: ${app.status}\n`

  if (app.description) {
    content += `\n**Descripcion:**\n${app.description}\n`
  }

  if (app.currentVersion) content += `\n- Version Actual: ${app.currentVersion}\n`
  if (app.technologies?.length) content += `- Tecnologias: ${app.technologies.join(', ')}\n`
  if (app.repositoryUrl) content += `- Repositorio: ${app.repositoryUrl}\n`
  if (app.productionUrl) content += `- URL Produccion: ${app.productionUrl}\n`
  if (app.stagingUrl) content += `- URL Staging: ${app.stagingUrl}\n`

  if (app.versionHistory?.length) {
    content += `\n**Historial de Versiones:**\n`
    app.versionHistory.forEach((v) => {
      content += `- v${v.version} (${v.type})`
      if (v.releaseDate) content += ` - ${formatDateValue(v.releaseDate)}`
      if (v.notes) content += `: ${v.notes}`
      content += '\n'
    })
  }

  return content
}
